import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Square, Trash2, Volume2, VolumeX } from "lucide-react";
import { Link } from "react-router-dom";
import { Chip, Label, MetaRow, Panel, PanelHeader, StatusDot, ComingSoonBox } from "../components/primitives";
import { GuardianSim } from "../components/GuardianSim";
import { WalletPanel } from "../components/WalletPanel";
import { DataStatus } from "../components/DataStatus";
import { sfx, isMuted, setMuted } from "../lib/sound";
import { apiGet, wsStreamUrl, usePolling, timeAgo } from "../lib/api";

const STRATEGIES = ["Momentum", "Mean Reversion", "Macro Overlay", "Volatility Target"];
const RISKS = ["Low", "Medium", "High"];
const TERM_AGENTS = ["ANALYST-01", "TRADER-07", "GUARDIAN-11", "QUANT-21", "EXECUTOR"];

const stamp = () => new Date().toTimeString().slice(0, 8);
const fmtTs = (ts) => new Date(ts * 1000).toTimeString().slice(0, 8);

const mapEvent = (ev) => {
  switch (ev.type) {
    case "block.indexed":
      return { t: fmtTs(ev.ts), tag: "BLOCK", msg: `Block #${ev.blockNumber?.toLocaleString()} indexed · ${ev.txCount} txs`, src: ev.source };
    case "tx.confirmed":
      return { t: fmtTs(ev.ts), tag: "CHAIN", msg: `Transaction ${ev.hash} confirmed in block #${ev.blockNumber?.toLocaleString()}`, src: ev.source };
    case "market.updated":
      return { t: fmtTs(ev.ts), tag: "MARKET", msg: `${ev.count} stock tokens synced — ${ev.source}`, src: ev.source };
    case "rpc.error":
      return { t: fmtTs(ev.ts), tag: "RPC", msg: "RPC connection error — retrying", src: ev.source };
    case "indexer.syncing":
      return { t: fmtTs(ev.ts), tag: "INDEXER", msg: `Syncing — block #${ev.indexedBlock?.toLocaleString()} of #${ev.latestBlock?.toLocaleString()} (${ev.blocksBehind?.toLocaleString()} behind)`, src: ev.source };
    default:
      return { t: fmtTs(ev.ts), tag: "SYSTEM", msg: ev.message || ev.type, src: ev.source || "SYSTEM" };
  }
};

const tagColor = (tag) =>
  tag === "BLOCK" ? "text-pulse-soft" : tag === "MARKET" ? "text-brass-bright" : tag === "RPC" ? "text-red-400" : tag === "SIM" ? "text-brass" : "text-fog-400";

const buildSimLines = (agentId, capital, risk, strategy) => [
  { tag: "SIM", msg: `Simulation initialized — ${strategy} / ${risk} risk / $${capital.toLocaleString()} capital.` },
  { tag: "SIM", msg: `${agentId}: context loaded. Analyzing mandate against portfolio state.` },
  { tag: "SIM", msg: "QUANT-21: allocation proposal generated. Weights normalized to mandate bounds." },
  { tag: "SIM", msg: risk === "High" ? "GUARDIAN-11: risk constraints flagged — proposal scaled down." : "GUARDIAN-11: risk constraints validated. All checks pass." },
  { tag: "SIM", msg: "EXECUTOR: execution simulated. No transaction broadcast — SIMULATION MODE." },
];

const ChainPanel = () => {
  const { data: blocks } = usePolling("/chain/blocks?limit=8", 8000);
  const { data: idx } = usePolling("/indexer/status", 8000);
  const { data: coverage } = usePolling("/chain/coverage", 30000);
  const head = blocks?.blocks?.[0];
  return (
    <Panel>
      <PanelHeader right={
        idx ? (
          <DataStatus
            status={idx.status === "SYNCED" ? "LIVE" : idx.status === "ERROR" ? "OFFLINE" : "SYNCING"}
            label={idx.status === "SYNCED" ? "Synchronized" : idx.status}
            sub={idx.blocksBehind != null ? `${idx.blocksBehind} behind` : ""}
          />
        ) : <DataStatus status="SYNCING" label="Indexer" />
      }>
        Chain Indexer
      </PanelHeader>
      <div className="p-4" data-testid="chain-panel">
        {!blocks ? (
          <div className="space-y-1.5">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-7 animate-pulse bg-white/[0.03]" />)}</div>
        ) : blocks.blocks.length === 0 ? (
          <p className="font-mono text-[11px] text-fog-600">Indexer starting — first blocks incoming.</p>
        ) : (
          blocks.blocks.map((b) => (
            <div key={b.block_number} className="flex items-center justify-between border-b border-white/[0.05] py-1.5 last:border-0" data-testid={`block-row-${b.block_number}`}>
              <span className="font-mono text-[11px] font-bold text-pulse-soft">#{b.block_number.toLocaleString()}</span>
              <span className={`font-mono text-[9px] uppercase tracking-[0.12em] ${b.confirmationStatus === "finalized" ? "text-pulse-soft" : b.confirmationStatus === "confirmed" ? "text-brass-bright" : "text-fog-600"}`}>
                {b.confirmationStatus || "observed"}
              </span>
              <span className="font-mono text-[10px] text-fog-600">{b.transaction_count} txs</span>
              <span className="font-mono text-[10px] text-fog-600">{timeAgo(b.timestamp)}</span>
            </div>
          ))
        )}
        {coverage && (
          <div className="mt-3 border-t border-white/[0.06] pt-3 font-mono text-[9px] uppercase tracking-[0.15em] text-fog-600" data-testid="chain-coverage">
            Coverage {coverage.coverage} · from #{coverage.earliestIndexedBlock?.toLocaleString()} · finalized #{idx?.finalizedBlock?.toLocaleString() || "—"}
          </div>
        )}
      </div>
    </Panel>
  );
};

export default function Terminal() {
  const [tab, setTab] = useState("sim");
  const [events, setEvents] = useState([]);
  const [streamLive, setStreamLive] = useState(false);
  const [running, setRunning] = useState(false);
  const [agentId, setAgentId] = useState("TRADER-07");
  const [capital, setCapital] = useState(25000);
  const [risk, setRisk] = useState("Medium");
  const [strategy, setStrategy] = useState("Momentum");
  const [result, setResult] = useState(null);
  const [muted, setMutedState] = useState(isMuted());
  const logRef = useRef(null);
  const timers = useRef([]);
  const lastEventId = useRef(0);
  const seenIds = useRef(new Set());
  const hadConnection = useRef(false);
  const [reconnects, setReconnects] = useState(0);
  const { data: chainStatus, error: chainError } = usePolling("/chain/status", 8000);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [events]);

  useEffect(() => {
    let ws;
    let closed = false;
    let retry;
    let attempt = 0;

    const refetchSnapshot = async () => {
      try {
        const seed = await apiGet("/events?limit=30");
        if (closed) return;
        seenIds.current = new Set(seed.events.map((e) => e.id));
        lastEventId.current = Math.max(0, ...seed.events.map((e) => e.id));
        setEvents(seed.events.map(mapEvent));
      } catch {}
    };

    const ingest = (raw) => {
      if (raw.type === "stream.heartbeat") return;
      if (raw.type === "stream.gap") {
        refetchSnapshot();
        return;
      }
      if (raw.id) {
        if (seenIds.current.has(raw.id)) return;
        seenIds.current.add(raw.id);
        if (seenIds.current.size > 400) seenIds.current = new Set([...seenIds.current].slice(-200));
        lastEventId.current = Math.max(lastEventId.current, raw.id);
      }
      setEvents((prev) => [...prev.slice(-80), mapEvent(raw)]);
      sfx.tick();
    };

    const connect = async () => {
      if (!lastEventId.current) await refetchSnapshot();
      try {
        const resume = hadConnection.current && lastEventId.current;
        ws = new WebSocket(`${wsStreamUrl()}${resume ? `?lastEventId=${lastEventId.current}` : ""}`);
        ws.onopen = () => {
          if (closed) return;
          setStreamLive(true);
          attempt = 0;
          if (hadConnection.current) setReconnects((r) => r + 1);
          hadConnection.current = true;
        };
        ws.onmessage = (m) => {
          try { ingest(JSON.parse(m.data)); } catch {}
        };
        ws.onclose = () => {
          if (closed) return;
          setStreamLive(false);
          const delay = Math.min(1000 * 2 ** attempt, 15000) + Math.random() * 400;
          attempt += 1;
          retry = setTimeout(connect, delay);
        };
      } catch {
        if (!closed) {
          const delay = Math.min(1000 * 2 ** attempt, 15000) + Math.random() * 400;
          attempt += 1;
          retry = setTimeout(connect, delay);
        }
      }
    };
    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      ws && ws.close();
    };
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) sfx.confirm();
  };

  const run = () => {
    if (running) return;
    setRunning(true);
    setResult(null);
    const lines = buildSimLines(agentId, capital, risk, strategy);
    lines.forEach((l, i) => {
      timers.current.push(setTimeout(() => {
        setEvents((prev) => [...prev.slice(-80), { t: stamp(), ...l, src: "SIMULATION" }]);
        sfx.tick();
      }, 650 * (i + 1)));
    });
    timers.current.push(setTimeout(() => {
      const base = { Momentum: [35, 25, 20, 20], "Mean Reversion": [25, 30, 25, 20], "Macro Overlay": [20, 25, 35, 20], "Volatility Target": [20, 20, 30, 30] }[strategy];
      setResult({
        alloc: [
          { sym: "NVDA", w: base[0] }, { sym: "AAPL", w: base[1] }, { sym: "SPY", w: base[2] }, { sym: "USDG", w: base[3] },
        ],
        riskLevel: risk === "High" ? "Medium (scaled)" : risk,
        agentDecision: `${agentId} proposes ${strategy.toLowerCase()} allocation over $${capital.toLocaleString()} notional.`,
        guardian: risk === "High" ? "SCALE DOWN — SIMULATION" : "APPROVED — SIMULATION",
        execution: "SIMULATED — NOT BROADCAST",
      });
      setRunning(false);
      sfx.confirm();
    }, 650 * lines.length + 500));
  };

  const stop = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
    setRunning(false);
    setEvents((prev) => [...prev, { t: stamp(), tag: "SIM", msg: "Simulation halted by operator.", src: "SIMULATION" }]);
    sfx.tick();
  };

  const tabs = [["sim", "Simulation"], ["live", "Live Data"], ["onchain", "Onchain"]];

  const chainStatusNode = chainError ? (
    <DataStatus status="OFFLINE" label="API Unreachable" />
  ) : chainStatus ? (
    <DataStatus
      status={chainStatus.rpcStatus === "error" ? "OFFLINE" : chainStatus.syncStatus === "synchronized" ? "LIVE" : "SYNCING"}
      label="Robinhood Chain"
      sub={chainStatus.blockNumber ? `Block #${chainStatus.blockNumber.toLocaleString()}` : "indexing…"}
    />
  ) : (
    <DataStatus status="SYNCING" label="Connecting" />
  );

  return (
    <main className="min-h-screen pt-16" data-testid="terminal-page">
      <div className="border-b border-white/[0.06] bg-ink-850">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center justify-between gap-4 px-5 py-5 md:px-8">
          <div>
            <div className="flex items-center gap-3">
              <StatusDot />
              <h1 className="font-display text-xl font-extrabold tracking-[0.15em] text-fog-100 sm:text-2xl">SYNAPTIX TERMINAL</h1>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1" data-testid="terminal-chain-status">
              {chainStatusNode}
              <DataStatus status={streamLive ? "LIVE" : "SYNCING"} label="Event Stream" pulse={streamLive} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              data-testid="sound-toggle"
              aria-label={muted ? "Unmute terminal sounds" : "Mute terminal sounds"}
              className={`flex h-[34px] w-[38px] items-center justify-center border transition-colors ${
                muted ? "border-white/10 text-fog-600 hover:text-fog-100" : "border-pulse/40 bg-pulse/10 text-pulse-soft"}`}
            >
              {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            </button>
            <div className="flex border border-white/[0.1]" data-testid="terminal-tabs">
              {tabs.map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => { setTab(id); sfx.tick(); }}
                  data-testid={`terminal-tab-${id}`}
                  className={`relative px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors ${
                    tab === id ? "text-ink-900" : "text-fog-400 hover:text-fog-100"}`}
                >
                  {tab === id && <motion.span layoutId="term-tab" className="absolute inset-0 bg-pulse" />}
                  <span className="relative z-10">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {tab !== "sim" ? (
        <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-8" data-testid="terminal-coming-soon">
          <div className="mx-auto max-w-md">
            <ComingSoonBox label={tab === "live" ? "Live Market Prices" : "Onchain Execution"} />
            <p className="mt-6 text-center text-sm leading-relaxed text-fog-400">
              {tab === "live"
                ? "No verified public price source for Robinhood stock tokens is integrated yet. Live prices activate when a verified feed is connected — no estimated data is shown."
                : "Onchain execution activates when vault contracts are deployed on Robinhood Chain. No transactions are currently possible."}
            </p>
            <button onClick={() => setTab("sim")} data-testid="terminal-back-to-sim"
              className="mx-auto mt-8 block border border-pulse/30 bg-pulse/10 px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-pulse-soft hover:bg-pulse/20">
              Return to Terminal
            </button>
          </div>
        </div>
      ) : (
        <div className="mx-auto grid max-w-[1440px] gap-4 px-5 py-6 md:px-8 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-3">
            <Panel>
              <PanelHeader right={<DataStatus status="NOT DEPLOYED" pulse={false} />}>Agents</PanelHeader>
              <div className="p-2" data-testid="terminal-agent-list">
                {TERM_AGENTS.map((id) => (
                  <button
                    key={id}
                    onClick={() => { setAgentId(id); sfx.tick(); }}
                    data-testid={`terminal-agent-${id.toLowerCase()}`}
                    className={`flex w-full items-center justify-between px-3 py-2.5 font-mono text-[11px] tracking-[0.1em] transition-colors ${
                      agentId === id ? "bg-pulse/10 text-pulse-soft" : "text-fog-400 hover:bg-white/[0.03] hover:text-fog-100"}`}
                  >
                    {id}
                    <StatusDot tone={agentId === id ? "green" : "muted"} />
                  </button>
                ))}
              </div>
              <p className="border-t border-white/[0.06] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-fog-600">
                Agents not deployed — selection drives the local simulator only
              </p>
            </Panel>

            <Panel>
              <PanelHeader right={<Chip tone="gold">Simulation</Chip>}>Strategy Config</PanelHeader>
              <div className="space-y-4 p-4">
                <div>
                  <div className="mb-1.5 flex justify-between font-mono text-[10px] uppercase tracking-[0.18em]">
                    <span className="text-fog-600">Capital</span>
                    <span className="text-pulse-soft">${capital.toLocaleString()}</span>
                  </div>
                  <input type="range" min="1000" max="100000" step="1000" value={capital}
                    onChange={(e) => setCapital(+e.target.value)}
                    data-testid="sim-capital-slider" className="syn-slider w-full" />
                </div>
                <div>
                  <Label className="mb-1.5">Risk</Label>
                  <div className="grid grid-cols-3 gap-1" data-testid="sim-risk-select">
                    {RISKS.map((r) => (
                      <button key={r} onClick={() => { setRisk(r); sfx.tick(); }} data-testid={`sim-risk-${r.toLowerCase()}`}
                        className={`border py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${
                          risk === r ? "border-pulse/50 bg-pulse/10 text-pulse-soft" : "border-white/10 text-fog-600 hover:text-fog-100"}`}>
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5">Strategy</Label>
                  <div className="grid grid-cols-2 gap-1" data-testid="sim-strategy-select">
                    {STRATEGIES.map((s) => (
                      <button key={s} onClick={() => { setStrategy(s); sfx.tick(); }} data-testid={`sim-strategy-${s.toLowerCase().replace(/\s/g, "-")}`}
                        className={`border px-2 py-1.5 font-mono text-[9px] uppercase tracking-[0.1em] transition-colors ${
                          strategy === s ? "border-brass/50 bg-brass/10 text-brass-bright" : "border-white/10 text-fog-600 hover:text-fog-100"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={run} disabled={running} data-testid="run-simulation"
                    className="flex flex-1 items-center justify-center gap-2 bg-pulse py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-ink-900 transition-all hover:bg-pulse-soft disabled:opacity-50">
                    <Play className="h-3 w-3" /> {running ? "Running" : "Run Simulation"}
                  </button>
                  {running && (
                    <button onClick={stop} data-testid="stop-simulation"
                      className="border border-brass/40 px-3 text-brass-bright hover:bg-brass/10" aria-label="Stop">
                      <Square className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Link to="/leaderboard" data-testid="terminal-leaderboard-link"
                  className="block border-t border-white/[0.06] pt-3 text-center font-mono text-[9px] uppercase tracking-[0.2em] text-fog-600 transition-colors hover:text-pulse-soft">
                  Agent Leaderboard →
                </Link>
              </div>
            </Panel>
          </div>

          <div className="space-y-4 lg:col-span-6">
            <WalletPanel />

            <AnimatePresence>
              {result && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <Panel data-testid="sim-result">
                    <PanelHeader right={<Chip tone="gold">Simulation Mode</Chip>}>Simulation Result</PanelHeader>
                    <div className="grid gap-0 sm:grid-cols-2">
                      <div className="border-b border-white/[0.08] p-4 sm:border-b-0 sm:border-r">
                        <Label className="mb-2">Projected Allocation</Label>
                        {result.alloc.map((a) => (
                          <div key={a.sym} className="mb-2">
                            <div className="mb-1 flex justify-between font-mono text-[11px]">
                              <span className="text-fog-400">{a.sym}</span><span className="text-fog-100">{a.w}%</span>
                            </div>
                            <div className="h-1 w-full bg-white/[0.06]">
                              <motion.div initial={{ width: 0 }} animate={{ width: `${a.w}%` }} transition={{ duration: 0.6 }}
                                className="h-full bg-gradient-to-r from-pulse to-brass" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="p-4">
                        <MetaRow k="Risk Level" v={result.riskLevel} />
                        <MetaRow k="Agent Decision" v={result.agentDecision} mono={false} />
                        <MetaRow k="Guardian" v={result.guardian} accent />
                        <MetaRow k="Execution" v={result.execution} />
                      </div>
                    </div>
                  </Panel>
                </motion.div>
              )}
            </AnimatePresence>

            <Panel className="scanlines relative">
              <PanelHeader right={
                <button onClick={() => setEvents([])} data-testid="clear-logs"
                  className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-fog-600 hover:text-fog-100">
                  <Trash2 className="h-3 w-3" /> Clear
                </button>
              }>
                <span className="flex items-center gap-2">
                  Live Event Stream
                  <DataStatus status={streamLive ? "LIVE" : "SYNCING"} pulse={streamLive} />
                </span>
              </PanelHeader>
              <div ref={logRef} className="h-72 overflow-y-auto p-4 font-mono text-[11px] leading-relaxed" data-testid="terminal-log">
                {events.length === 0 && (
                  <p className="text-fog-600">Connecting to the SYNAPTIX event stream — real indexer and market events appear here.</p>
                )}
                {events.map((l, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} className="flex gap-3 py-1">
                    <span className="shrink-0 text-fog-600">[{l.t}]</span>
                    <span className={`shrink-0 font-bold ${tagColor(l.tag)}`}>{l.tag}</span>
                    <span className="text-fog-400">{l.msg}</span>
                    {l.src && <span className="ml-auto hidden shrink-0 text-[9px] uppercase tracking-[0.15em] text-fog-600 xl:inline">{l.src}</span>}
                  </motion.div>
                ))}
                {running && (
                  <div className="flex gap-3 py-1">
                    <span className="text-fog-600">[{stamp()}]</span>
                    <span className="animate-pulse text-pulse-soft">▊</span>
                  </div>
                )}
              </div>
            </Panel>
          </div>

          <div className="space-y-4 lg:col-span-3">
            <ChainPanel />
            <GuardianSim compact />
          </div>
        </div>
      )}
    </main>
  );
}
